import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Stethoscope, Pill, Star, Users, ShieldCheck, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SearchBar from '../components/common/SearchBar';
import ListingCard from '../components/common/ListingCard';
import { listingsAPI, statsAPI, seedAPI } from '../lib/api';

const Home = () => {
  const [featuredListings, setFeaturedListings] = useState([]);
  const [stats, setStats] = useState({ facilities: 0, specialists: 0, pharmacies: 0, reviews: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Try to seed the database first (will skip if already seeded)
        await seedAPI.seed();
        
        // Load featured listings
        const listingsRes = await listingsAPI.getAll({ featured: true, limit: 6 });
        setFeaturedListings(listingsRes.data);
        
        // Load stats
        const statsRes = await statsAPI.get();
        setStats(statsRes.data);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  const categories = [
    {
      id: 'facilities',
      title: 'Facilities',
      description: 'Hospitals, clinics, urgent care centers',
      icon: Building2,
      color: 'facility',
      link: '/facilities',
      count: stats.facilities,
    },
    {
      id: 'specialists',
      title: 'Specialists',
      description: 'Doctors and medical providers',
      icon: Stethoscope,
      color: 'specialist',
      link: '/specialists',
      count: stats.specialists,
    },
    {
      id: 'pharmacies',
      title: 'Pharmacies',
      description: 'Local and specialty pharmacies',
      icon: Pill,
      color: 'pharmacy',
      link: '/pharmacies',
      count: stats.pharmacies,
    },
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Search',
      description: 'Find healthcare providers by specialty, location, or name',
    },
    {
      step: 2,
      title: 'Compare',
      description: 'Read reviews, check ratings, and compare options',
    },
    {
      step: 3,
      title: 'Connect',
      description: 'Call, get directions, or book an appointment',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section gradient-hero" data-testid="hero-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-16 md:py-24">
          <div className="text-center mb-12 animate-fade-in">
            <Badge className="mb-4 bg-teal-100 text-teal-700 px-4 py-1">
              Trusted Healthcare Directory
            </Badge>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
              Find <span className="text-gradient">trusted care</span> near you
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              Search thousands of healthcare providers, read verified reviews, and find the care you deserve.
            </p>
          </div>

          {/* Search Bar */}
          <div className="flex justify-center animate-slide-up stagger-1">
            <SearchBar variant="hero" />
          </div>

          {/* Trust Signals */}
          <div className="flex flex-wrap justify-center gap-6 md:gap-12 mt-12 text-sm text-gray-600 animate-slide-up stagger-2">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-teal-600" />
              <span><strong>{stats.facilities + stats.specialists + stats.pharmacies}+</strong> Providers</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              <span><strong>{stats.reviews}+</strong> Reviews</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-teal-600" />
              <span>Verified Listings</span>
            </div>
          </div>
        </div>
      </section>

      {/* Category Cards */}
      <section className="py-16 md:py-24 bg-white" data-testid="categories-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-gray-900 mb-4">
              Browse by Category
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Find the right healthcare provider for your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={category.link}
                className={`category-card ${category.color}`}
                data-testid={`category-${category.id}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                    category.color === 'facility' ? 'bg-teal-200' :
                    category.color === 'specialist' ? 'bg-blue-200' : 'bg-rose-200'
                  }`}>
                    <category.icon className={`w-7 h-7 ${
                      category.color === 'facility' ? 'text-teal-700' :
                      category.color === 'specialist' ? 'text-blue-700' : 'text-rose-700'
                    }`} />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-xl text-gray-900">{category.title}</h3>
                    <p className="text-sm text-gray-500">{category.count} listings</p>
                  </div>
                </div>
                <p className="text-gray-600 mb-4">{category.description}</p>
                <div className="flex items-center text-teal-700 font-medium">
                  Browse {category.title}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Listings */}
      <section className="py-16 md:py-24 bg-gray-50" data-testid="featured-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-gray-900 mb-2">
                Featured Providers
              </h2>
              <p className="text-gray-600">Top-rated healthcare providers in your area</p>
            </div>
            <Link to="/search">
              <Button variant="outline" className="hidden md:flex">
                View All
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl h-80 skeleton" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredListings.map((listing) => (
                <ListingCard key={listing.listing_id} listing={listing} />
              ))}
            </div>
          )}

          <div className="flex justify-center mt-8 md:hidden">
            <Link to="/search">
              <Button variant="outline">
                View All Providers
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-white" data-testid="how-it-works-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Finding quality healthcare has never been easier
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorks.map((item, index) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-teal-100 flex items-center justify-center">
                  <span className="font-heading font-bold text-2xl text-teal-700">{item.step}</span>
                </div>
                <h3 className="font-heading font-semibold text-xl text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
                {index < howItWorks.length - 1 && (
                  <div className="hidden md:block absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2">
                    <ArrowRight className="w-6 h-6 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-teal-700" data-testid="cta-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Are you a healthcare provider?
          </h2>
          <p className="text-teal-100 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of providers on MedSearcher. List your practice for free or upgrade for premium features.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/add-listing">
              <Button size="lg" className="bg-white text-teal-700 hover:bg-gray-100 rounded-full px-8">
                Add Your Listing
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-teal-600 rounded-full px-8">
                View Pricing
              </Button>
            </Link>
          </div>
          <div className="flex justify-center gap-8 mt-8 text-teal-100 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>Free Basic Listing</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>Verified Badge</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>Analytics Dashboard</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
